import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useLoadAppData } from '../../hooks/useLoadAppData';
import { getRoleLevel } from '../../utils/permissions';
import MoveStatus from '../../components/MoveStatus/MoveStatus';
import { setSelectedAccount } from '../../store/ui';
import { fetchTrailersUser } from '../../store/trailers';
import { generateReport, exportToExcel } from '../../utils/printReport';


const Home = () => {
    const history = useHistory();
    const [activeTab, setActiveTab] = useState('trailers');
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const appDataLoading = useLoadAppData();
    const sessionUser = useSelector(state => state.session.user);
    const searchQuery = useSelector((state) => state.ui.searchQuery);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [locationHistoryData, setLocationHistoryData] = useState({});
    const [truckLocationHistoryData, setTruckLocationHistoryData] = useState({});
    const refreshTimerRef = useRef(null);
    const truckRefreshTimerRef = useRef(null);
    const dispatch = useDispatch();
    // Store all trailers locally to avoid disappearing when companies change
    const [cachedTrailers, setCachedTrailers] = useState({});
    const [loadingTrailers, setLoadingTrailers] = useState(false);
    const [loadingLocationHistory, setLoadingLocationHistory] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    // Track which accounts we've already attempted to load
    const loadedAccountsRef = useRef({});

    // Add new filter states
    const [selectedLocation, setSelectedLocation] = useState('all');
    const [selectedTag, setSelectedTag] = useState('all');
    const [selectedLoadType, setSelectedLoadType] = useState('all');
    const [timeSort, setTimeSort] = useState('asc');

    // Add truck filter states
    const [selectedTruckLocation, setSelectedTruckLocation] = useState('all');
    const [selectedTruckTag, setSelectedTruckTag] = useState('all');
    const [selectedTruckAssignedUser, setSelectedTruckAssignedUser] = useState('all');

    // Add user filter states
    const [selectedUserRole, setSelectedUserRole] = useState('all');
    const [selectedUserStatus, setSelectedUserStatus] = useState('all');

    // Get Redux state selectors outside of useMemo
    const trailersState = useSelector((state) => state.trailers);

    // Then use useMemo to process the trailers data in a stable way
    const trailersFromStore = useMemo(() => {
        if (!trailersState) return [];
        if (Array.isArray(trailersState)) return trailersState;
        if (typeof trailersState === 'object') {
            // Filter out the "available" key and any non-object values
            return Object.entries(trailersState)
                .filter(([key, value]) => key !== 'available' && typeof value === 'object')
                .map(([_, value]) => value);
        }
        return [];
    }, [trailersState]);

    // Get all data from store
    const accounts = useSelector((state) => state.accounts);
    const trucks = useSelector((state) => Array.isArray(state.trucks) ? state.trucks : Object.values(state.trucks) || []);
    const buildings = useSelector((state) => Array.isArray(state.buildings) ? state.buildings : Object.values(state.buildings) || []);
    const yards = useSelector((state) => Array.isArray(state.yards) ? state.yards : Object.values(state.yards) || []);
    const users = useSelector((state) => Array.isArray(state.users) ? state.users : Object.values(state.users) || []);

    // Determine if we're still loading initial app data
    const isAppDataLoading = appDataLoading;

    // Define section-specific loading states
    const isTrailersLoading = loadingTrailers || (activeTab === 'trailers' && !initialDataLoaded);
    const isTrucksLoading = activeTab === 'trucks' && !initialDataLoaded;
    const isYardsLoading = activeTab === 'yards' && !initialDataLoaded;
    const isBuildingsLoading = activeTab === 'buildings' && !initialDataLoaded;
    const isUsersLoading = activeTab === 'users' && !initialDataLoaded;

    // Cache trailers from the store when they change
    useEffect(() => {
        if (trailersFromStore.length > 0) {
            // Group trailers by account_id for quick access
            const trailersByAccount = {};
            trailersFromStore.forEach(trailer => {
                if (trailer.account_id) {
                    if (!trailersByAccount[trailer.account_id]) {
                        trailersByAccount[trailer.account_id] = [];
                    }
                    trailersByAccount[trailer.account_id].push(trailer);
                }
            });

            // Check if we need to update the cache
            let needsUpdate = false;

            // Compare the new trailers with the cached ones
            Object.keys(trailersByAccount).forEach(accountId => {
                // If we don't have this account cached yet, we need to update
                if (!cachedTrailers[accountId]) {
                    needsUpdate = true;
                    return; // Skip further checks for this account
                }

                const cachedTrailersForAccount = cachedTrailers[accountId];
                const newTrailersForAccount = trailersByAccount[accountId];

                // If trailer counts differ, we need to update
                if (cachedTrailersForAccount.length !== newTrailersForAccount.length) {
                    needsUpdate = true;
                    return; // Skip further checks for this account
                }

                // Create maps for detailed comparison
                const cachedTrailerMap = new Map(cachedTrailersForAccount.map(t => [t.id, t]));
                const newTrailerMap = new Map(newTrailersForAccount.map(t => [t.id, t]));

                // Check if all new trailer IDs are in the cache and if properties changed
                for (const [id, newTrailer] of newTrailerMap) {
                    const cachedTrailer = cachedTrailerMap.get(id);
                    if (!cachedTrailer) {
                        needsUpdate = true;
                        return; // Skip further checks once we find a difference
                    }
                    
                    // Check if trailer properties have changed (especially location)
                    if (cachedTrailer.trailer_location !== newTrailer.trailer_location ||
                        cachedTrailer.is_loaded !== newTrailer.is_loaded ||
                        cachedTrailer.red_tagged !== newTrailer.red_tagged ||
                        cachedTrailer.yellow_tagged !== newTrailer.yellow_tagged ||
                        cachedTrailer.user_id !== newTrailer.user_id) {
                        needsUpdate = true;
                        return; // Skip further checks once we find a difference
                    }
                }
            });

            // Only update if needed
            if (needsUpdate) {
                // Update cached trailers - always update with latest data
                setCachedTrailers(prevCache => {
                    // Create a new object to avoid mutating the previous state
                    const newCache = { ...prevCache };

                    // For each account in the new data, replace the entire array of trailers
                    // This ensures we have the latest version of each trailer
                    Object.keys(trailersByAccount).forEach(accountId => {
                        newCache[accountId] = trailersByAccount[accountId];
                    });

                    return newCache;
                });
            }
        }
    }, [trailersFromStore, cachedTrailers]);

    // Load trailers for the selected account if not already cached
    useEffect(() => {
        if (!selectedAccountId) return;

        const loadTrailersForAccount = async () => {
            // If we don't have trailers for this account yet or fewer than expected
            // AND we haven't already tried to load them
            if ((!cachedTrailers[selectedAccountId] || cachedTrailers[selectedAccountId].length === 0) &&
                !loadedAccountsRef.current[selectedAccountId]) {
                // Mark this account as attempted to be loaded
                loadedAccountsRef.current[selectedAccountId] = true;
                setLoadingTrailers(true);
                try {
                    // Fetch trailers for this account
                    await dispatch(fetchTrailersUser(selectedAccountId));
                } catch (error) {
                    console.error('Error fetching trailers for account:', error);
                } finally {
                    setLoadingTrailers(false);
                }
            }
        };

        loadTrailersForAccount();
    }, [selectedAccountId, dispatch]); // Removed cachedTrailers from dependencies

    // Set up the initial account and mark the data as loaded
    useEffect(() => {
        if (!sessionUser) {
            history.push('/login');
            return;
        }

        let initialAccountId = null;

        if (sessionUser?.is_admin) {
            // For admin users, set the first account as default if none is selected
            if (!selectedAccountId && accounts.length > 0) {
                initialAccountId = accounts[0].id;
                setSelectedAccountId(initialAccountId);
                dispatch(setSelectedAccount(initialAccountId));
            }
        } else if (sessionUser?.account_id && !selectedAccountId) {
            // For regular users, set their assigned account
            initialAccountId = sessionUser.account_id;
            setSelectedAccountId(initialAccountId);
            dispatch(setSelectedAccount(initialAccountId));
        }

        // Load initial data for the account if we have an account ID
        const loadInitialData = async () => {
            if (!initialAccountId) return;

            setInitialDataLoaded(false);

            try {
                // Load trailers if needed
                if (!cachedTrailers[initialAccountId] || cachedTrailers[initialAccountId].length === 0) {
                    setLoadingTrailers(true);
                    await dispatch(fetchTrailersUser(initialAccountId));
                    setLoadingTrailers(false);
                }

                // Load location history
                setLoadingLocationHistory(true);
                await fetchLocationHistoryData(initialAccountId);
                await fetchTruckLocationHistoryData(initialAccountId);
                setLoadingLocationHistory(false);

            } catch (error) {
                console.error('Error loading initial data:', error);
            } finally {
                // Mark initial data as loaded, regardless of errors
                setInitialDataLoaded(true);
            }
        };

        if (initialAccountId && !initialDataLoaded) {
            loadInitialData();
        }
    }, [sessionUser, accounts, selectedAccountId, history, dispatch, initialDataLoaded]);

    useEffect(() => {
        const handleScroll = () => {
            // Show button when user scrolls down 300px
            setShowScrollButton(window.scrollY > 300);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Function to fetch location history data
    const fetchLocationHistoryData = async (accountId) => {
        if (!accountId) return;

        try {
            // Fetch all location histories for the account at once
            const response = await fetch(`/api/accounts/${accountId}/trailers-location-history`);
            if (response.ok) {
                const data = await response.json();
                if (data && Object.keys(data).length > 0) {
                    setLocationHistoryData(prevData => ({
                        ...prevData,
                        ...data
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching location history data:', error);
        }
    };

    // Function to fetch truck location history data
    const fetchTruckLocationHistoryData = async (accountId) => {
        if (!accountId) return;

        try {
            // Fetch all truck location histories for the account at once
            const response = await fetch(`/api/accounts/${accountId}/trucks-location-history`);
            if (response.ok) {
                const data = await response.json();
                if (data && Object.keys(data).length > 0) {
                    setTruckLocationHistoryData(prevData => ({
                        ...prevData,
                        ...data
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching truck location history data:', error);
        }
    };

    // Modify the trailer location history fetching useEffect
    useEffect(() => {
        // Clear any existing timer
        if (refreshTimerRef.current) {
            clearInterval(refreshTimerRef.current);
        }

        let mounted = true;

        // Set up a timer to refresh every 30 seconds
        refreshTimerRef.current = setInterval(() => {
            if (mounted && selectedAccountId) {
                fetchLocationHistoryData(selectedAccountId);
            }
        }, 30000);

        // Clean up the timer when the component unmounts or when dependencies change
        return () => {
            mounted = false;
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [selectedAccountId]);

    // Modify the truck location history fetching useEffect
    useEffect(() => {
        // Clear any existing timer
        if (truckRefreshTimerRef.current) {
            clearInterval(truckRefreshTimerRef.current);
        }

        let mounted = true;

        // Set up a timer to refresh every 5 minutes (300000 ms)
        truckRefreshTimerRef.current = setInterval(() => {
            if (mounted && selectedAccountId) {
                fetchTruckLocationHistoryData(selectedAccountId);
            }
        }, 300000);

        // Clean up the timer when the component unmounts or when dependencies change
        return () => {
            mounted = false;
            if (truckRefreshTimerRef.current) {
                clearInterval(truckRefreshTimerRef.current);
                truckRefreshTimerRef.current = null;
            }
        };
    }, [selectedAccountId]);

    // Get trailers for the selected account - first from cache, fallback to store filter
    const getAccountTrailers = () => {
        if (cachedTrailers[selectedAccountId] && cachedTrailers[selectedAccountId].length > 0) {
            return cachedTrailers[selectedAccountId];
        }
        // Fallback to filtering from the store
        return trailersFromStore.filter(trailer => trailer.account_id === selectedAccountId);
    };

    // Filter data for selected account - use cached trailers for trailers
    const accountTrucks = selectedAccountId ? trucks.filter(truck => truck.account_id === selectedAccountId) : [];
    const accountTrailers = selectedAccountId ? getAccountTrailers() : [];
    const accountYards = selectedAccountId ? yards.filter(yard => yard.account_id === selectedAccountId) : [];
    const accountBuildings = selectedAccountId ? buildings.filter(building => building.account_id === selectedAccountId) : [];
    const accountUsers = selectedAccountId ? users.filter(user => {
        // First filter by account - include users with matching account_id OR null account_id (admin users)
        if (user.account_id !== selectedAccountId && user.account_id !== null) return false;

        // Then filter by role level - only show users with equal or lower role level
        const sessionUserLevel = getRoleLevel(sessionUser);
        const userLevel = getRoleLevel(user);

        return userLevel <= sessionUserLevel;
    }) : [];

    // Check if user has manager or admin privileges
    const hasManagerAccess = sessionUser?.is_admin ||
        sessionUser?.is_super_manager ||
        sessionUser?.is_account_manager ||
        sessionUser?.is_manager;

    // Check if user can add new items (admin or super manager only)
    const canAdd = sessionUser?.is_admin || sessionUser?.is_super_manager || sessionUser?.is_account_manager || sessionUser?.is_manager

    // Check if user can print reports (manager or above)
    const canPrintReports = sessionUser?.is_admin || sessionUser?.is_super_manager || sessionUser?.is_account_manager || sessionUser?.is_manager

    // Modify the tabs array to conditionally include users
    const tabs = ['trailers', 'trucks', 'yards', 'buildings'];
    if (hasManagerAccess) {
        tabs.push('users');
    }

    const getStatusColor = (item) => {
        if (item.red_tagged) return 'bg-red-100 text-red-800';
        if (item.yellow_tagged) return 'bg-yellow-100 text-yellow-800';
        if (item.in_shop) return 'bg-blue-100 text-blue-800';
        return 'bg-green-100 text-green-800';
    };

    const getStatusText = (item) => {
        if (item.red_tagged) return item.tagged_description || 'Red Tagged';
        if (item.yellow_tagged) return item.tagged_description || 'Yellow Tagged';
        if (item.in_shop) return 'Shop';
        return 'Available';
    };

    // Get current location duration for a trailer
    const getCurrentLocationDuration = (trailerId) => {
        if (!trailerId) return 'N/A';

        const locationHistory = locationHistoryData[trailerId];
        if (!locationHistory || locationHistory.length === 0) {
            return 'N/A';
        }

        const currentLocation = locationHistory[0];
        if (!currentLocation) return 'N/A';

        // If there's an exit time, the trailer is no longer at this location
        if (currentLocation.exit_time) return 'Moved';

        // Return the formatted duration if available
        if (currentLocation.current_duration_formatted) {
            // Replace the formatted duration with abbreviated units
            const formattedDuration = currentLocation.current_duration_formatted;
            return formattedDuration
                .replace(' days', 'd')
                .replace(' day', 'd')
                .replace(' hours', 'h')
                .replace(' hour', 'h')
                .replace(' minutes', 'm')
                .replace(' minute', 'm')
                .replace(' mins', 'm')
                .replace(' min', 'm');
        }

        // If we have an entry time but no formatted duration, calculate a simple duration
        if (currentLocation.entry_time) {
            const entryTime = new Date(currentLocation.entry_time);
            const now = new Date();
            const diffMs = now - entryTime;

            // Calculate days, hours, minutes
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const diffHrs = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

            // Format with abbreviated units
            if (diffDays > 0) {
                return `${diffDays}d ${diffHrs}h`;
            } else if (diffHrs > 0) {
                return `${diffHrs}h ${diffMins}m`;
            } else {
                return `${diffMins}m`;
            }
        }

        return 'Just arrived';
    };

    // Get current location duration for a truck
    const getTruckLocationDuration = (truckId) => {
        if (!truckId) return 'N/A';

        const locationHistory = truckLocationHistoryData[truckId];
        if (!locationHistory || locationHistory.length === 0) {
            return 'N/A';
        }

        const currentLocation = locationHistory[0];
        if (!currentLocation) return 'N/A';

        // If there's an exit time, the truck is no longer at this location
        if (currentLocation.exit_time) return 'Moved';

        // Return the formatted duration if available
        if (currentLocation.current_duration_formatted) {
            // Replace the formatted duration with abbreviated units
            const formattedDuration = currentLocation.current_duration_formatted;
            return formattedDuration
                .replace(' days', 'd')
                .replace(' day', 'd')
                .replace(' hours', 'h')
                .replace(' hour', 'h')
                .replace(' minutes', 'm')
                .replace(' minute', 'm')
                .replace(' mins', 'm')
                .replace(' min', 'm');
        }

        // If we have an entry time but no formatted duration, calculate a simple duration
        if (currentLocation.entry_time) {
            const entryTime = new Date(currentLocation.entry_time);
            const now = new Date();
            const diffMs = now - entryTime;

            // Calculate days, hours, minutes
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const diffHrs = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

            // Format with abbreviated units
            if (diffDays > 0) {
                return `${diffDays}d ${diffHrs}h`;
            } else if (diffHrs > 0) {
                return `${diffHrs}h ${diffMins}m`;
            } else {
                return `${diffMins}m`;
            }
        }

        return 'Just arrived';
    };

    // Generate unique building name options for the selected account
    const generateLocationOptions = () => {
        // Get unique building names (before the dash)
        const buildingNames = accountBuildings
            .map(b => b.building_name)
            .filter(Boolean);
        // Get yard names
        const yardNames = accountYards
            .map(y => y.name)
            .filter(Boolean);
        // Remove duplicates and combine both arrays
        return [...new Set([...buildingNames, ...yardNames])].sort();
    };

    // Generate unique truck location options from actual truck locations
    const generateTruckLocationOptions = () => {
        // Extract unique locations that are actually used by trucks
        const truckLocations = accountTrucks
            .map(truck => truck.truck_location?.toLowerCase())
            .filter(Boolean); // Remove undefined/null/empty values

        // Create a unique set of locations
        const uniqueLocations = [...new Set(truckLocations)];

        // Sort locations alphabetically
        return uniqueLocations.sort();
    };

    // Generate unique user role options
    const generateUserRoleOptions = () => {
        const roles = [
            { value: 'super_manager', label: 'Super Manager' },
            { value: 'account_manager', label: 'Account Manager' },
            { value: 'manager', label: 'Manager' },
            { value: 'admin', label: 'Admin' },
            { value: 'security', label: 'Security' },
            { value: 'mechanic', label: 'Mechanic' },
            { value: 'road_driver', label: 'Road Driver' },
            { value: 'yard_driver', label: 'Yard Driver' }
        ];

        // Only include roles that exist in accountUsers
        return roles.filter(role =>
            accountUsers.some(user => user[`is_${role.value}`])
        );
    };

    // Helper functions to calculate trailer counts for location filters
    const getLocationCounts = () => {
        const counts = {
            all: accountTrailers.length,
            road: 0,
            shop: 0,
            yard: 0
        };

        // Count road, shop, and yard trailers
        accountTrailers.forEach(trailer => {
            const location = trailer.trailer_location?.toLowerCase() || '';
            if (location.includes('road')) counts.road++;
            if (location.includes('shop')) counts.shop++;
            if (location.includes('yard')) counts.yard++;
        });

        // Count trailers for each specific location
        generateLocationOptions().forEach(locationOption => {
            const locationKey = locationOption.toLowerCase();
            counts[locationKey] = accountTrailers.filter(trailer => {
                const location = trailer.trailer_location?.toLowerCase() || '';
                return location.includes(locationKey);
            }).length;
        });

        return counts;
    };

    // Use useMemo to ensure locationCounts updates when accountTrailers changes
    const locationCounts = useMemo(() => getLocationCounts(), [accountTrailers, generateLocationOptions]);

    // Generate unique truck assigned users options
    const generateTruckAssignedUsersOptions = () => {
        // Get all user IDs that are assigned to trucks
        const assignedUserIds = accountTrucks
            .map(truck => truck.assigned_user_id)
            .filter(Boolean); // Remove undefined/null values

        // Get unique user IDs
        const uniqueUserIds = [...new Set(assignedUserIds)];

        // Find the actual users from the users array
        const assignedUsers = uniqueUserIds
            .map(userId => users.find(user => user.id === userId))
            .filter(Boolean); // Remove any not found

        // Sort by alpha code
        return assignedUsers.sort((a, b) =>
            a.alpha_code?.localeCompare(b.alpha_code || '') || 0
        );
    };

    // Filter trailers based on selected filters and search query
    const filteredTrailers = accountTrailers.filter((trailer) => {
        const matchesSearch = !searchQuery?.trim() ||
            trailer.trailer_location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            trailer.trailer_number?.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
            trailer.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            trailer.license_plate?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            trailer.company_owner?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            trailer.load_number?.toString().toLowerCase().includes(searchQuery.toLowerCase());

        const location = trailer.trailer_location?.toLowerCase() || '';
        const matchesLocation = selectedLocation === 'all' ||
            (selectedLocation === 'road' && location.includes('road')) ||
            (selectedLocation === 'shop' && location.includes('shop')) ||
            (selectedLocation === 'yard' && location.includes('yard')) ||
            (generateLocationOptions().map(opt => opt.toLowerCase()).includes(selectedLocation.toLowerCase()) &&
                location.includes(selectedLocation.toLowerCase()));

        const matchesTag = selectedTag === 'all' ||
            (selectedTag === 'red' && trailer.red_tagged) ||
            (selectedTag === 'yellow' && trailer.yellow_tagged) ||
            (selectedTag === 'available' && !trailer.red_tagged && !trailer.yellow_tagged);

        const matchesLoadType = selectedLoadType === 'all' ||
            (selectedLoadType === 'loaded' && trailer.is_loaded) ||
            (selectedLoadType === 'empty' && !trailer.is_loaded && !trailer.is_tray) ||
            (selectedLoadType === 'trays' && trailer.is_tray);

        return matchesSearch && matchesLocation && matchesTag && matchesLoadType;
    });

    // Filter trucks based on search query and filters
    const filteredTrucks = accountTrucks.filter((truck) => {
        const query = searchQuery?.trim();
        const matchesSearch = !query || (
            (truck.truck_location &&
                truck.truck_location.toLowerCase().includes(query.toLowerCase())) ||
            (truck.truck_number &&
                String(truck.truck_number).toLowerCase().includes(query.toLowerCase())) ||
            (truck.type &&
                truck.type.toLowerCase().includes(query.toLowerCase())) ||
            (truck.make &&
                truck.make.toLowerCase().includes(query.toLowerCase())) ||
            (truck.model &&
                truck.model.toLowerCase().includes(query.toLowerCase()))
        );

        const truckLocation = truck.truck_location?.toLowerCase() || '';
        const matchesTruckLocation = selectedTruckLocation === 'all' ||
            (selectedTruckLocation === 'road' && truckLocation.includes('road')) ||
            (selectedTruckLocation === 'shop' && truckLocation.includes('shop')) ||
            (generateLocationOptions().map(opt => opt.toLowerCase()).includes(selectedTruckLocation.toLowerCase()) &&
                truckLocation.includes(selectedTruckLocation.toLowerCase()));

        const matchesTruckTag = selectedTruckTag === 'all' ||
            (selectedTruckTag === 'red' && truck.red_tagged) ||
            (selectedTruckTag === 'yellow' && truck.yellow_tagged) ||
            (selectedTruckTag === 'available' && !truck.red_tagged && !truck.yellow_tagged);

        const matchesTruckAssignedUser = selectedTruckAssignedUser === 'all' ||
            (selectedTruckAssignedUser === 'unassigned' && !truck.assigned_user_id) ||
            (truck.assigned_user_id && truck.assigned_user_id.toString() === selectedTruckAssignedUser);

        return matchesSearch && matchesTruckLocation && matchesTruckTag && matchesTruckAssignedUser;
    });

    // Filter buildings based on search query
    const filteredBuildings = accountBuildings.filter((building) => {
        const query = searchQuery?.trim();
        if (!query) return true;
        const lowerQuery = query.toLowerCase();

        return (
            (building.building_name &&
                building.building_name.toLowerCase().includes(lowerQuery)) ||
            (building.location &&
                building.location.toLowerCase().includes(lowerQuery)) ||
            (building.building_door_number &&
                String(building.building_door_number).toLowerCase().includes(lowerQuery))
        );
    });

    // Filter yards based on search query
    const filteredYards = accountYards.filter((yard) => {
        const query = searchQuery?.trim();
        if (!query) return true;
        const lowerQuery = query.toLowerCase();

        return (
            (yard.name &&
                yard.name.toLowerCase().includes(lowerQuery)) ||
            (yard.parking_location &&
                yard.parking_location.toLowerCase().includes(lowerQuery))
        );
    });

    // Filter users based on search query and filters
    const filteredUsers = accountUsers.filter((user) => {
        const query = searchQuery?.trim();
        const matchesSearch = !query || (
            (user.alpha_code &&
                user.alpha_code.toLowerCase().includes(query.toLowerCase())) ||
            (user.email &&
                user.email.toLowerCase().includes(query.toLowerCase())) ||
            (user.role &&
                user.role.toLowerCase().includes(query.toLowerCase()))
        );

        const matchesUserRole = selectedUserRole === 'all' ||
            (selectedUserRole === 'super_manager' && user.is_super_manager) ||
            (selectedUserRole === 'account_manager' && user.is_account_manager) ||
            (selectedUserRole === 'manager' && user.is_manager) ||
            (selectedUserRole === 'admin' && user.is_admin) ||
            (selectedUserRole === 'security' && user.is_security) ||
            (selectedUserRole === 'mechanic' && user.is_mechanic) ||
            (selectedUserRole === 'road_driver' && user.is_road_driver) ||
            (selectedUserRole === 'yard_driver' && user.is_yard_driver);

        const matchesUserStatus = selectedUserStatus === 'all' ||
            (selectedUserStatus === 'active' && user.is_active) ||
            (selectedUserStatus === 'inactive' && !user.is_active);

        return matchesSearch && matchesUserRole && matchesUserStatus;
    });

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Add a function to get duration in minutes for sorting
    const getDurationInMinutes = (trailerId) => {
        if (!trailerId) return 0;
        const locationHistory = locationHistoryData[trailerId];
        if (!locationHistory || locationHistory.length === 0) return 0;

        const currentLocation = locationHistory[0];
        if (!currentLocation || currentLocation.exit_time) return 0;

        if (currentLocation.entry_time) {
            const entryTime = new Date(currentLocation.entry_time);
            const now = new Date();
            return Math.floor((now - entryTime) / (1000 * 60)); // Convert to minutes
        }
        return 0;
    };

    // Sort trailers by time at location
    const sortTrailersByTime = (trailers) => {
        return [...trailers].sort((a, b) => {
            const timeA = getDurationInMinutes(a.id);
            const timeB = getDurationInMinutes(b.id);
            return timeSort === 'asc' ? timeA - timeB : timeB - timeA;
        });
    };

    // Update the filtered trailers to include sorting
    const filteredAndSortedTrailers = React.useMemo(() => {
        return sortTrailersByTime(filteredTrailers);
    }, [filteredTrailers, timeSort, locationHistoryData]);

    // Reset function to reset all filters when tab changes
    useEffect(() => {
        // Reset filters when tab changes
        setSelectedLocation('all');
        setSelectedTag('all');
        setSelectedLoadType('all');
        setTimeSort('asc');
        setSelectedTruckLocation('all');
        setSelectedTruckTag('all');
        setSelectedTruckAssignedUser('all');
        setSelectedUserRole('all');
        setSelectedUserStatus('all');
    }, [activeTab]);

    // Only show the no account selected message if we're not in the initial loading state
    if (!selectedAccountId && !isAppDataLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="text-3xl text-red-600">
                    No account selected. Please contact support if this persists.
                </div>
            </div>
        );
    }

    return (
        <div className="p-5 min-h-[calc(100vh-100px)] flex flex-col">
            {sessionUser?.is_admin && (
                <div className="w-1/2 mx-auto mb-8">
                    <div className="flex items-center gap-4">
                        <span className="text-4xl font-bold min-w-max">Accounts across ALL Companies:</span>
                        <div className="flex-1 relative h-[68px]"> {/* Added fixed height container */}
                            <select
                                id="company-select"
                                value={selectedAccountId || ''}
                                onChange={async (e) => {
                                    const newAccountId = Number(e.target.value);

                                    // Prevent setting empty/invalid account ID
                                    if (!newAccountId) {
                                        return; // Exit early if no valid account ID is selected
                                    }

                                    setSelectedAccountId(newAccountId);

                                    // Explicitly fetch trailers for the new account if not cached
                                    if (!cachedTrailers[newAccountId] || cachedTrailers[newAccountId].length === 0) {
                                        setLoadingTrailers(true);
                                        try {
                                            await dispatch(fetchTrailersUser(newAccountId));
                                        } catch (error) {
                                            console.error('Error fetching trailers for new account:', error);
                                        } finally {
                                            setLoadingTrailers(false);
                                        }
                                    }

                                    dispatch(setSelectedAccount(newAccountId));
                                }}
                                className="w-full h-full p-4 border border-gray-300 rounded-lg shadow-sm text-3xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Select Company</option>
                                {accounts.map((account, index) => (
                                    <option key={`account-${account.id || ''}-${index}`} value={account.id}>
                                        {account.account_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            <h1 className="text-8xl font-semi mb-5 text-center text-blue-700">
                {accounts.find(acc => acc.id === selectedAccountId)?.account_name} Dashboard
            </h1>
            <MoveStatus onRefreshNeeded={() => selectedAccountId && fetchLocationHistoryData(selectedAccountId)} />
            <div className="bg-white rounded-lg shadow p-8">
                {/* Add loading indicator for trailers */}
                {activeTab === 'trailers' && loadingTrailers && (
                    <div className="flex justify-center items-center p-10">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
                        <div className="ml-4 text-2xl text-blue-600">Loading...</div>
                    </div>
                )}

                <div className="flex justify-between items-center mb-6">
                    <div className="flex gap-4 h-[40px]"> {/* Added fixed height */}
                        {tabs.map(tab => (
                            <button
                                key={tab}
                                id={tab}
                                className={`px-6 py-3 rounded-lg text-2xl font-semibold transform transition-all duration-200 ${
                                    activeTab === tab
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-md'
                                }`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </div>

                    <div className="flex gap-4 h-[40px]"> {/* Added fixed height */}
                        {activeTab === 'trailers' && canAdd && (
                            <>
                                <button
                                    id="add-trailer-button"
                                    className="px-6 py-3 bg-green-600 text-white rounded-lg text-xl font-semibold hover:bg-green-700 transition-all duration-200 flex items-center"
                                    onClick={() => history.push('/trailers/new')}
                                >
                                    <span className="mr-2">+</span> Add New Trailer
                                </button>
                                <button
                                    className="px-6 py-3 bg-blue-600 text-white rounded-lg text-xl font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center"
                                    onClick={() => history.push('/trailers/bulk-upload')}
                                >
                                    <span className="mr-2">↑</span> Bulk Upload Trailers
                                </button>
                                {canPrintReports && (
                                    <>
                                        <button
                                            id="print-trailers-report"
                                            className="px-6 py-3 bg-purple-600 text-white rounded-lg text-xl font-semibold hover:bg-purple-700 transition-all duration-200 flex items-center"
                                            onClick={() => generateReport('trailers', filteredAndSortedTrailers, {
                                                title: `${accounts.find(acc => acc.id === selectedAccountId)?.account_name} - Trailers Report`,
                                                subtitle: `Account: ${accounts.find(acc => acc.id === selectedAccountId)?.account_name}`
                                            })}
                                        >
                                            <span className="material-icons mr-2">Print</span> Trailers Report
                                        </button>
                                        <button
                                            id="excel-trailers-report"
                                            className="px-6 py-3 bg-[#217346] hover:bg-[#185a36] text-white rounded-lg text-xl font-semibold transition-all duration-200 flex items-center"
                                            onClick={() => exportToExcel('trailers', filteredAndSortedTrailers, {
                                                title: `${accounts.find(acc => acc.id === selectedAccountId)?.account_name} - Trailers Report`,
                                                subtitle: `Account: ${accounts.find(acc => acc.id === selectedAccountId)?.account_name}`
                                            })}
                                        >
                                            <span className="material-icons mr-2">Download</span> Trailers Excel Report
                                        </button>
                                    </>
                                )}
                            </>
                        )}

                        {/* Similar button sections for trucks, yards, buildings, users */}
                        {activeTab === 'trucks' && canAdd && (
                            <>
                                <button
                                    id="add-truck-button"
                                    className="px-6 py-3 bg-green-600 text-white rounded-lg text-xl font-semibold hover:bg-green-700 transition-all duration-200 flex items-center"
                                    onClick={() => history.push('/trucks/new')}
                                >
                                    <span className="mr-2">+</span> Add New Truck
                                </button>
                                {canPrintReports && (
                                    <>
                                        <button
                                            id="print-trucks-report"
                                            className="px-6 py-3 bg-purple-600 text-white rounded-lg text-xl font-semibold hover:bg-purple-700 transition-all duration-200 flex items-center"
                                            onClick={() => generateReport('trucks', filteredTrucks, {
                                                title: `${accounts.find(acc => acc.id === selectedAccountId)?.account_name} - Trucks Report`,
                                                subtitle: `Account: ${accounts.find(acc => acc.id === selectedAccountId)?.account_name}`
                                            })}
                                        >
                                            <span className="material-icons mr-2">Print</span> Trucks Report
                                        </button>
                                        <button
                                            id="excel-trucks-report"
                                            className="px-6 py-3 bg-[#217346] hover:bg-[#185a36] text-white rounded-lg text-xl font-semibold transition-all duration-200 flex items-center"
                                            onClick={() => exportToExcel('trucks', filteredTrucks, {
                                                title: `${accounts.find(acc => acc.id === selectedAccountId)?.account_name} - Trucks Report`,
                                                subtitle: `Account: ${accounts.find(acc => acc.id === selectedAccountId)?.account_name}`
                                            })}
                                        >
                                            <span className="material-icons mr-2">Download</span> Trucks Excel Report
                                        </button>
                                    </>
                                )}
                            </>
                        )}

                        {activeTab === 'users' && hasManagerAccess && (
                            <>
                                <button
                                    id="add-user-button"
                                    className="px-6 py-3 bg-green-600 text-white rounded-lg text-xl font-semibold hover:bg-green-700 transition-all duration-200 flex items-center"
                                    onClick={() => history.push('/signup')}
                                >
                                    <span className="mr-2">+</span> Add New User
                                </button>
                                {canPrintReports && (
                                    <>
                                        <button
                                            id="print-users-report"
                                            className="px-6 py-3 bg-purple-600 text-white rounded-lg text-xl font-semibold hover:bg-purple-700 transition-all duration-200 flex items-center"
                                            onClick={() => generateReport('users', filteredUsers, {
                                                title: `${accounts.find(acc => acc.id === selectedAccountId)?.account_name} - Users Report`,
                                                subtitle: `Account: ${accounts.find(acc => acc.id === selectedAccountId)?.account_name}`
                                            })}
                                        >
                                            <span className="material-icons mr-2">Print</span> Users Report
                                        </button>
                                        <button
                                            id="excel-users-report"
                                            className="px-6 py-3 bg-[#217346] hover:bg-[#185a36] text-white rounded-lg text-xl font-semibold transition-all duration-200 flex items-center"
                                            onClick={() => exportToExcel('users', filteredUsers, {
                                                title: `${accounts.find(acc => acc.id === selectedAccountId)?.account_name} - Users Report`,
                                                subtitle: `Account: ${accounts.find(acc => acc.id === selectedAccountId)?.account_name}`
                                            })}
                                        >
                                            <span className="material-icons mr-2">Download</span> Users Excel Report
                                        </button>
                                    </>
                                )}
                            </>
                        )}

                        {activeTab === 'yards' && hasManagerAccess && (
                            <>
                                <button
                                    id="add-yard-button"
                                    className="px-6 py-3 bg-green-600 text-white rounded-lg text-xl font-semibold hover:bg-green-700 transition-all duration-200 flex items-center"
                                    onClick={() => history.push('/yards/new')}
                                >
                                    <span className="mr-2">+</span> Add New Yard
                                </button>
                                {canPrintReports && (
                                    <>
                                        <button
                                            id="print-yards-report"
                                            className="px-6 py-3 bg-purple-600 text-white rounded-lg text-xl font-semibold hover:bg-purple-700 transition-all duration-200 flex items-center"
                                            onClick={() => generateReport('yards', filteredYards, {
                                                title: `${accounts.find(acc => acc.id === selectedAccountId)?.account_name} - Yards Report`,
                                                subtitle: `Account: ${accounts.find(acc => acc.id === selectedAccountId)?.account_name}`
                                            })}
                                        >
                                            <span className="material-icons mr-2">Print</span> Yards Report
                                        </button>
                                        <button
                                            id="excel-yards-report"
                                            className="px-6 py-3 bg-[#217346] hover:bg-[#185a36] text-white rounded-lg text-xl font-semibold transition-all duration-200 flex items-center"
                                            onClick={() => exportToExcel('yards', filteredYards, {
                                                title: `${accounts.find(acc => acc.id === selectedAccountId)?.account_name} - Yards Report`,
                                                subtitle: `Account: ${accounts.find(acc => acc.id === selectedAccountId)?.account_name}`
                                            })}
                                        >
                                            <span className="material-icons mr-2">Download</span> Yards Excel Report
                                        </button>
                                    </>
                                )}
                            </>
                        )}

                        {activeTab === 'buildings' && hasManagerAccess && (
                            <>
                                <button
                                    id="add-building-button"
                                    className="px-6 py-3 bg-green-600 text-white rounded-lg text-xl font-semibold hover:bg-green-700 transition-all duration-200 flex items-center"
                                    onClick={() => history.push('/buildings/new')}
                                >
                                    <span className="mr-2">+</span> Add New Building
                                </button>
                                {canPrintReports && (
                                    <>
                                        <button
                                            id="print-buildings-report"
                                            className="px-6 py-3 bg-purple-600 text-white rounded-lg text-xl font-semibold hover:bg-purple-700 transition-all duration-200 flex items-center"
                                            onClick={() => generateReport('buildings', filteredBuildings, {
                                                title: `${accounts.find(acc => acc.id === selectedAccountId)?.account_name} - Buildings Report`,
                                                subtitle: `Account: ${accounts.find(acc => acc.id === selectedAccountId)?.account_name}`
                                            })}
                                        >
                                            <span className="material-icons mr-2">Print</span> Buildings Report
                                        </button>
                                        <button
                                            id="excel-buildings-report"
                                            className="px-6 py-3 bg-[#217346] hover:bg-[#185a36] text-white rounded-lg text-xl font-semibold transition-all duration-200 flex items-center"
                                            onClick={() => exportToExcel('buildings', filteredBuildings, {
                                                title: `${accounts.find(acc => acc.id === selectedAccountId)?.account_name} - Buildings Report`,
                                                subtitle: `Account: ${accounts.find(acc => acc.id === selectedAccountId)?.account_name}`
                                            })}
                                        >
                                            <span className="material-icons mr-2">Download</span> Buildings Excel Report
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Enhanced Filter Section */}
                {activeTab === 'trailers' && (
                    <div className="mb-6 bg-gray-50 p-4 rounded-lg shadow-sm">
                        <div className="flex items-center gap-6">
                            {/* Main filters container */}
                            <div className="flex flex-1 gap-6">
                                {/* Location Filter */}
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold mb-2 text-gray-800">Location</h3>
                                    <div className="flex flex-wrap gap-3">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="location-all"
                                                value="all"
                                                checked={selectedLocation === 'all'}
                                                onChange={(e) => setSelectedLocation(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">All ({locationCounts.all})</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="location-road"
                                                value="road"
                                                checked={selectedLocation === 'road'}
                                                onChange={(e) => setSelectedLocation(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">Road ({locationCounts.road})</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="location-shop"
                                                value="shop"
                                                checked={selectedLocation === 'shop'}
                                                onChange={(e) => setSelectedLocation(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">Shop ({locationCounts.shop})</span>
                                        </label>

                                        {generateLocationOptions().map((location, index) => (
                                            <label key={`location-${location}-${index}`} className="inline-flex items-center">
                                                <input
                                                    type="radio"
                                                    id={`location-${location}`}
                                                    value={location}
                                                    checked={selectedLocation === location}
                                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                                    className="form-radio h-6 w-6 text-blue-600"
                                                />
                                                <span className="ml-2 text-lg capitalize">{location} ({locationCounts[location.toLowerCase()] || 0})</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Vertical Separator */}
                                <div className="w-px bg-gray-300 self-stretch"></div>

                                {/* Status Filter */}
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold mb-2 text-gray-800">Status</h3>
                                    <div className="flex flex-wrap gap-3">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="status-all"
                                                value="all"
                                                checked={selectedTag === 'all'}
                                                onChange={(e) => setSelectedTag(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">All</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="status-red"
                                                value="red"
                                                checked={selectedTag === 'red'}
                                                onChange={(e) => setSelectedTag(e.target.value)}
                                                className="form-radio h-6 w-6 text-red-600"
                                            />
                                            <span className="ml-2 text-lg text-red-600">Red Tagged</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="status-yellow"
                                                value="yellow"
                                                checked={selectedTag === 'yellow'}
                                                onChange={(e) => setSelectedTag(e.target.value)}
                                                className="form-radio h-6 w-6 text-yellow-600"
                                            />
                                            <span className="ml-2 text-lg text-yellow-600">Yellow Tagged</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="status-available"
                                                value="available"
                                                checked={selectedTag === 'available'}
                                                onChange={(e) => setSelectedTag(e.target.value)}
                                                className="form-radio h-6 w-6 text-green-600"
                                            />
                                            <span className="ml-2 text-lg text-green-600">Available</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Vertical Separator */}
                                <div className="w-px bg-gray-300 self-stretch"></div>

                                {/* Load Type Filter */}
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold mb-2 text-gray-800">Load Type</h3>
                                    <div className="flex flex-wrap gap-3">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="loadType-all"
                                                value="all"
                                                checked={selectedLoadType === 'all'}
                                                onChange={(e) => setSelectedLoadType(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">All</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="loadType-loaded"
                                                value="loaded"
                                                checked={selectedLoadType === 'loaded'}
                                                onChange={(e) => setSelectedLoadType(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">Loaded</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="loadType-empty"
                                                value="empty"
                                                checked={selectedLoadType === 'empty'}
                                                onChange={(e) => setSelectedLoadType(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">Empty</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="loadType-trays"
                                                value="trays"
                                                checked={selectedLoadType === 'trays'}
                                                onChange={(e) => setSelectedLoadType(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">With Trays</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Vertical Separator */}
                                <div className="w-px bg-gray-300 self-stretch"></div>

                                {/* Time at Location Filter */}
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold mb-2 text-gray-800">Time at Location</h3>
                                    <div className="flex flex-wrap gap-3">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="timeSort-asc"
                                                value="asc"
                                                checked={timeSort === 'asc'}
                                                onChange={(e) => setTimeSort(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">Newest</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="timeSort-desc"
                                                value="desc"
                                                checked={timeSort === 'desc'}
                                                onChange={(e) => setTimeSort(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">Oldest</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Vertical Separator */}
                            <div className="w-px bg-gray-300 self-stretch"></div>

                            {/* Reset Button */}
                            <div className="flex items-center pl-6">
                                <button
                                    onClick={() => {
                                        setSelectedLocation('all');
                                        setSelectedTag('all');
                                        setSelectedLoadType('all');
                                        setTimeSort('asc');
                                    }}
                                    className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-lg font-semibold transition-all duration-200 flex items-center gap-2 h-12"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                    </svg>
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Trucks Filter Section */}
                {activeTab === 'trucks' && (
                    <div className="mb-6 bg-gray-50 p-4 rounded-lg shadow-sm">
                        <div className="flex items-center gap-6">
                            {/* Main filters container */}
                            <div className="flex flex-1 gap-6">
                                {/* Location Filter */}
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold mb-2 text-gray-800">Location</h3>
                                    <div className="flex flex-wrap gap-3">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="truck-location-all"
                                                value="all"
                                                checked={selectedTruckLocation === 'all'}
                                                onChange={(e) => setSelectedTruckLocation(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">All</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="truck-location-road"
                                                value="road"
                                                checked={selectedTruckLocation === 'road'}
                                                onChange={(e) => setSelectedTruckLocation(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">Road</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="truck-location-shop"
                                                value="shop"
                                                checked={selectedTruckLocation === 'shop'}
                                                onChange={(e) => setSelectedTruckLocation(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">Shop</span>
                                        </label>
                                        {/* Dynamically generate options for truck locations based on buildings and yards */}
                                        {generateLocationOptions().map((location, index) => (
                                            <label key={`truck-location-${location}-${index}`} className="inline-flex items-center">
                                                <input
                                                    type="radio"
                                                    id={`truck-location-${location}`}
                                                    value={location}
                                                    checked={selectedTruckLocation === location}
                                                    onChange={(e) => setSelectedTruckLocation(e.target.value)}
                                                    className="form-radio h-6 w-6 text-blue-600"
                                                />
                                                <span className="ml-2 text-lg capitalize">{location}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Vertical Separator */}
                                <div className="w-px bg-gray-300 self-stretch"></div>

                                {/* Status Filter */}
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold mb-2 text-gray-800">Status</h3>
                                    <div className="flex flex-wrap gap-3">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="truck-status-all"
                                                value="all"
                                                checked={selectedTruckTag === 'all'}
                                                onChange={(e) => setSelectedTruckTag(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">All</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="truck-status-red"
                                                value="red"
                                                checked={selectedTruckTag === 'red'}
                                                onChange={(e) => setSelectedTruckTag(e.target.value)}
                                                className="form-radio h-6 w-6 text-red-600"
                                            />
                                            <span className="ml-2 text-lg text-red-600">Red Tagged</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="truck-status-yellow"
                                                value="yellow"
                                                checked={selectedTruckTag === 'yellow'}
                                                onChange={(e) => setSelectedTruckTag(e.target.value)}
                                                className="form-radio h-6 w-6 text-yellow-600"
                                            />
                                            <span className="ml-2 text-lg text-yellow-600">Yellow Tagged</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="truck-status-available"
                                                value="available"
                                                checked={selectedTruckTag === 'available'}
                                                onChange={(e) => setSelectedTruckTag(e.target.value)}
                                                className="form-radio h-6 w-6 text-green-600"
                                            />
                                            <span className="ml-2 text-lg text-green-600">Available</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Vertical Separator */}
                                <div className="w-px bg-gray-300 self-stretch"></div>

                                {/* Assigned User Filter */}
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold mb-2 text-gray-800">Assigned To</h3>
                                    <div className="flex flex-wrap gap-3">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="truck-assigned-all"
                                                value="all"
                                                checked={selectedTruckAssignedUser === 'all'}
                                                onChange={(e) => setSelectedTruckAssignedUser(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">All</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="truck-assigned-unassigned"
                                                value="unassigned"
                                                checked={selectedTruckAssignedUser === 'unassigned'}
                                                onChange={(e) => setSelectedTruckAssignedUser(e.target.value)}
                                                className="form-radio h-6 w-6 text-gray-600"
                                            />
                                            <span className="ml-2 text-lg">Unassigned</span>
                                        </label>
                                        {generateTruckAssignedUsersOptions().map((user, index) => (
                                            <label key={`truck-assigned-${user.id}-${index}`} className="inline-flex items-center">
                                                <input
                                                    type="radio"
                                                    id={`truck-assigned-${user.id}`}
                                                    value={user.id.toString()}
                                                    checked={selectedTruckAssignedUser === user.id.toString()}
                                                    onChange={(e) => setSelectedTruckAssignedUser(e.target.value)}
                                                    className="form-radio h-6 w-6 text-blue-600"
                                                />
                                                <span className="ml-2 text-lg">{user.alpha_code}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Vertical Separator */}
                            <div className="w-px bg-gray-300 self-stretch"></div>

                            {/* Reset Button */}
                            <div className="flex items-center pl-6">
                                <button
                                    onClick={() => {
                                        setSelectedTruckLocation('all');
                                        setSelectedTruckTag('all');
                                        setSelectedTruckAssignedUser('all');
                                    }}
                                    className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-lg font-semibold transition-all duration-200 flex items-center gap-2 h-12"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                    </svg>
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Users Filter Section */}
                {activeTab === 'users' && (
                    <div className="mb-6 bg-gray-50 p-4 rounded-lg shadow-sm">
                        <div className="flex items-center gap-6">
                            {/* Main filters container */}
                            <div className="flex flex-1 gap-6">
                                {/* Role Filter */}
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold mb-2 text-gray-800">Role</h3>
                                    <div className="flex flex-wrap gap-3">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="user-role-all"
                                                value="all"
                                                checked={selectedUserRole === 'all'}
                                                onChange={(e) => setSelectedUserRole(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">All</span>
                                        </label>
                                        {generateUserRoleOptions().map((role, index) => (
                                            <label key={`user-role-${role.value}-${index}`} className="inline-flex items-center">
                                                <input
                                                    type="radio"
                                                    id={`user-role-${role.value}`}
                                                    value={role.value}
                                                    checked={selectedUserRole === role.value}
                                                    onChange={(e) => setSelectedUserRole(e.target.value)}
                                                    className="form-radio h-6 w-6 text-blue-600"
                                                />
                                                <span className="ml-2 text-lg">{role.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Vertical Separator */}
                                <div className="w-px bg-gray-300 self-stretch"></div>

                                {/* Status Filter */}
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold mb-2 text-gray-800">Status</h3>
                                    <div className="flex flex-wrap gap-3">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="user-status-all"
                                                value="all"
                                                checked={selectedUserStatus === 'all'}
                                                onChange={(e) => setSelectedUserStatus(e.target.value)}
                                                className="form-radio h-6 w-6 text-blue-600"
                                            />
                                            <span className="ml-2 text-lg">All</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="user-status-active"
                                                value="active"
                                                checked={selectedUserStatus === 'active'}
                                                onChange={(e) => setSelectedUserStatus(e.target.value)}
                                                className="form-radio h-6 w-6 text-green-600"
                                            />
                                            <span className="ml-2 text-lg text-green-600">Active</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                id="user-status-inactive"
                                                value="inactive"
                                                checked={selectedUserStatus === 'inactive'}
                                                onChange={(e) => setSelectedUserStatus(e.target.value)}
                                                className="form-radio h-6 w-6 text-red-600"
                                            />
                                            <span className="ml-2 text-lg text-red-600">Inactive</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Vertical Separator */}
                            <div className="w-px bg-gray-300 self-stretch"></div>

                            {/* Reset Button */}
                            <div className="flex items-center pl-6">
                                <button
                                    onClick={() => {
                                        setSelectedUserRole('all');
                                        setSelectedUserStatus('all');
                                    }}
                                    className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-lg font-semibold transition-all duration-200 flex items-center gap-2 h-12"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                    </svg>
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Trailers Tab */}
                {activeTab === 'trailers' && (
                    <div className="loadTrailer">
                        <div id='trailer-header' className="loadTrailer_header font-sans">
                            <div className="flex items-center justify-center h-full">Location</div>
                            <div className="flex items-center justify-center h-full">Trailer</div>
                            <div className="flex items-center justify-center h-full">Type</div>
                            <div className="flex items-center justify-center h-full">State - Plate</div>
                            <div className="flex items-center justify-center h-full">Trailer Number</div>
                            <div className="flex items-center justify-center h-full">Time at Location</div>
                            <div className="flex items-center justify-center h-full">Load # OR Trays</div>
                            <div className="flex items-center justify-center h-full">Current User</div>
                            <div className="flex items-center justify-center h-full">Tag Description</div>
                        </div>

                        {/* Show loading indicator when trailers are loading */}
                        {isTrailersLoading ? (
                            <div className="flex justify-center items-center p-10">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mr-4"></div>
                                <div className="text-2xl text-blue-600">Loading...</div>
                            </div>
                        ) : filteredAndSortedTrailers.length === 0 ? (
                            <div className="text-center p-10 text-gray-500 text-xl">
                                No trailers found matching your filters
                            </div>
                        ) : (
                            filteredAndSortedTrailers.map((trailer, index) => (
                                <div id='trailer-row'
                                    className={`grid-row border-2 border-gray-200
                                        hover:shadow-xl hover:bg-blue-100 hover:border-blue-500
                                        hover:scale-[1.01] transition-all duration-200 cursor-pointer
                                        ${trailer?.red_tagged ? 'text-red-800' : ''}`}
                                    key={`trailer-${trailer?.id || ''}-${index}`}
                                    onClick={() => {
                                        console.log('Navigating to trailer with ID:', trailer?.id);
                                        if (trailer?.id) {
                                            history.push(`/trailers/${trailer.id}`);
                                        } else {
                                            console.error('Cannot navigate: trailer ID is undefined');
                                        }
                                    }}
                                >
                                    <div id='trailer-location'>{trailer?.trailer_location || "Unknown"}</div>
                                    <div className="trailer">
                                        <img
                                            src={
                                                trailer?.is_loaded
                                                    ? "/images/trailer-loadedYard.jpeg"
                                                    : trailer?.is_tray
                                                        ? "/images/trailer-traysFull.jpeg"
                                                        : "/images/trailer-empty.jpeg"
                                            }
                                            alt={
                                                trailer?.is_loaded
                                                    ? "Loaded Trailer"
                                                    : trailer?.is_tray
                                                        ? "Trailer with Trays"
                                                        : "Empty Trailer"
                                            }
                                            width="100"
                                            height="60"
                                            className="object-contain"
                                        />
                                    </div>
                                    <div id='trailer_type'>{trailer?.type || "N/A"}</div>
                                    <div id='license_plate'>{`${trailer?.license_state || ""} - ${trailer?.license_plate || ""}`}</div>
                                    <div id='trailer_number'>{trailer?.trailer_number || "N/A"}</div>
                                    <div id='location_duration'>{getCurrentLocationDuration(trailer?.id)}</div>
                                    <div id='load_number'>
                                        {trailer?.is_tray
                                            ? `${trailer?.tray_count} Tray${trailer?.tray_count !== 1 ? 's' : ''}`
                                            : trailer?.is_loaded
                                                ? trailer?.load_number
                                                : "Empty"}
                                    </div>
                                    <div id='current_user'>{
                                        // Find the user with matching ID and display their alphacode
                                        users.find(user => user.id === trailer?.user_id)?.alpha_code || "N/A"
                                    }</div>
                                    <div id='tagged_description'>
                                        <span
                                            className={`px-4 py-2 rounded-md ${getStatusColor(trailer)}
                                            break-words text-center w-full max-h-14 overflow-y-auto`}
                                        >
                                            {trailer?.tagged_description || ""}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Trucks Tab */}
                {activeTab === 'trucks' && (
                    <div className="loadTrailer">
                        <div id='truck-header' className="loadTrailer_header grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_2fr]">
                            <div className="flex items-center justify-center h-full">Location</div>
                            <div className="flex items-center justify-center h-full">Truck</div>
                            <div className="flex items-center justify-center h-full">Number</div>
                            <div className="flex items-center justify-center h-full">Type</div>
                            <div className="flex items-center justify-center h-full">Make</div>
                            <div className="flex items-center justify-center h-full">Model</div>
                            <div className="flex items-center justify-center h-full">Time at Location</div>
                            <div className="flex items-center justify-center h-full">Assigned To</div>
                            <div className="flex items-center justify-center h-full">Tag Description</div>
                        </div>

                        {/* Show loading indicator when trucks are loading */}
                        {isTrucksLoading ? (
                            <div className="flex justify-center items-center p-10">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mr-4"></div>
                                <div className="text-2xl text-blue-600">Loading...</div>
                            </div>
                        ) : filteredTrucks.length === 0 ? (
                            <div className="text-center p-10 text-gray-500 text-xl">
                                No trucks found matching your filters
                            </div>
                        ) : (
                            filteredTrucks.map((truck, index) => {
                                const assignedUser = users.find(u => u.id === truck?.assigned_user_id);
                                return (
                                    <div
                                        id='truck-row' className={`grid-row grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_2fr] border-2 border-gray-200 hover:shadow-xl
                        hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.01]
                        transition-all duration-200 cursor-pointer ${truck?.red_tagged ? 'text-red-800' : ''}`}
                                        key={`truck-${truck?.id || ''}-${index}`}
                                        onClick={() => history.push(`/trucks/${truck?.id}`)}
                                    >
                                        <div className="flex items-center justify-center">{truck?.truck_location || "Unknown"}</div>
                                        <div className="flex items-center justify-center">
                                            <img
                                                className="w-36"
                                                src="/images/truck.png"
                                                alt="Truck"
                                            />
                                        </div>
                                        <div className="flex items-center justify-center">{truck?.truck_number || "N/A"}</div>
                                        <div className="flex items-center justify-center">{truck?.type || "N/A"}</div>
                                        <div className="flex items-center justify-center">{truck?.make || "N/A"}</div>
                                        <div className="flex items-center justify-center">{truck?.model || "N/A"}</div>
                                        <div className="flex items-center justify-center">{getTruckLocationDuration(truck?.id)}</div>
                                        <div className="flex items-center justify-center">{assignedUser ? `${assignedUser.alpha_code}` : 'Not assigned'}</div>
                                        <div className="flex items-center justify-center">
                                            <span
                                                className={`px-4 py-2 rounded-md ${getStatusColor(truck)}
                            break-words text-center w-full max-h-14 overflow-y-auto`}
                                            >
                                                {truck?.tagged_description || getStatusText(truck)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* Buildings Tab */}
                {activeTab === 'buildings' && (
                    <div className="loadTrailer">
                        <div id='building-header' className="loadTrailer_header grid-cols-[1fr_1fr_2fr_1fr_1fr]">
                            <div className="flex items-center justify-center h-full">Name</div>
                            <div className="flex items-center justify-center h-full">Door Count</div>
                            {/* <div className="flex items-center justify-center h-full">Location</div> */}
                            <div className="flex items-center justify-center h-full">Created</div>
                            <div className="flex items-center justify-center h-full">Updated</div>
                        </div>

                        {/* Show loading indicator when buildings are loading */}
                        {isBuildingsLoading ? (
                            <div className="flex justify-center items-center p-10">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mr-4"></div>
                                <div className="text-2xl text-blue-600">Loading...</div>
                            </div>
                        ) : filteredBuildings.length === 0 ? (
                            <div className="text-center p-10 text-gray-500 text-xl">
                                No buildings found matching your filters
                            </div>
                        ) : (
                            filteredBuildings.map((building, index) => (
                                <div
                                    id='building-row' className="grid-row grid-cols-[1fr_1fr_2fr_1fr_1fr] border-2 border-gray-200 hover:shadow-xl
                      hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.01]
                      transition-all duration-200 cursor-pointer"
                                    key={`building-${building?.id || ''}-${index}`}
                                    onClick={() => history.push(`/buildings/${building?.id}`)}
                                >
                                    <div>{building?.building_name || "N/A"}</div>
                                    <div>{building?.building_door_number || "N/A"}</div>
                                    {/* <div>{building?.location || "N/A"}</div> */}
                                    <div>{building?.created_at ? new Date(building.created_at).toLocaleDateString() : "N/A"}</div>
                                    <div>{building?.updated_at ? new Date(building.updated_at).toLocaleDateString() : "N/A"}</div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Yards Tab */}
                {activeTab === 'yards' && (
                    <div className="loadTrailer">
                        <div id='yard-header' className="loadTrailer_header grid-cols-[1fr_1fr_2fr_1fr_1fr]">
                            <div className="flex items-center justify-center h-full">Name</div>
                            <div className="flex items-center justify-center h-full">Parking</div>
                            <div className="flex items-center justify-center h-full">Location</div>
                            <div className="flex items-center justify-center h-full">Created</div>
                            <div className="flex items-center justify-center h-full">Updated</div>
                        </div>

                        {/* Show loading indicator when yards are loading */}
                        {isYardsLoading ? (
                            <div className="flex justify-center items-center p-10">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mr-4"></div>
                                <div className="text-2xl text-blue-600">Loading...</div>
                            </div>
                        ) : filteredYards.length === 0 ? (
                            <div className="text-center p-10 text-gray-500 text-xl">
                                No yards found matching your filters
                            </div>
                        ) : (
                            filteredYards.map((yard, index) => (
                                <div
                                    id='yard-row' className="grid-row grid-cols-[1fr_1fr_2fr_1fr_1fr] border-2 border-gray-200 hover:shadow-xl
                      hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.01]
                      transition-all duration-200 cursor-pointer"
                                    key={`yard-${yard?.id || ''}-${index}`}
                                    onClick={() => history.push(`/yards/${yard?.id}`)}
                                >
                                    <div>{yard?.name || "N/A"}</div>
                                    <div>
                                        <span className={`px-4 py-2 rounded-full ${yard?.parking_spots ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {yard?.parking_spots ? 'Available' : 'None'}
                                        </span>
                                    </div>
                                    <div>{yard?.parking_location || "N/A"}</div>
                                    <div>{yard?.created_at ? new Date(yard.created_at).toLocaleDateString() : "N/A"}</div>
                                    <div>{yard?.updated_at ? new Date(yard.updated_at).toLocaleDateString() : "N/A"}</div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div id='user-header' className="loadTrailer">
                        <div className="my-4 text-gray-600 text-xl italic">
                            Note: You can only view users with equal or lower role levels than your own.
                        </div>
                        <div className="loadTrailer_header grid-cols-[1fr_2fr_1fr_1fr_1fr_1.5fr]">
                            <div className="flex items-center justify-center h-full">Alpha Code</div>
                            <div className="flex items-center justify-center h-full">Email</div>
                            <div className="flex items-center justify-center h-full">Role</div>
                            <div className="flex items-center justify-center h-full">Status</div>
                            <div className="flex items-center justify-center h-full">Created</div>
                            <div className="flex items-center justify-center h-full">Updated</div>
                        </div>

                        {/* Show loading indicator when users are loading */}
                        {isUsersLoading ? (
                            <div className="flex justify-center items-center p-10">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mr-4"></div>
                                <div className="text-2xl text-blue-600">Loading...</div>
                            </div>
                        ) : filteredUsers.length === 0 ? (
                            <div className="text-center p-10 text-gray-500 text-xl">
                                No users found matching your filters
                            </div>
                        ) : (
                            filteredUsers.map((user, index) => (
                                <div
                                    id='user-row'
                                    className={`grid-row grid-cols-[1fr_2fr_1fr_1fr_1fr_1.5fr] border-2 border-gray-200 hover:shadow-xl
                      hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.01]
                      transition-all duration-200 cursor-pointer ${!user?.is_active ? 'opacity-75' : ''}`}
                                    key={`user-${user?.id || ''}-${index}`}
                                    onClick={() => history.push(`/users/${user?.id}`)}
                                >
                                    <div>{user?.alpha_code || "N/A"}</div>
                                    <div>{user?.email || "N/A"}</div>
                                    <div>
                                        {user?.is_super_manager ? "Super Manager" :
                                            user?.is_account_manager ? "Account Manager" :
                                                user?.is_manager ? "Manager" :
                                                    user?.is_admin ? "Admin" :
                                                        user?.is_security ? "Security" :
                                                            user?.is_mechanic ? "Mechanic" :
                                                                user?.is_road_driver ? "Road Driver" :
                                                                    user?.is_yard_driver ? "Yard Driver" :
                                                                        "User"}
                                    </div>
                                    <div>
                                        <span className={`px-3 py-1 rounded-full text-sm ${user?.is_active
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                            }`}>
                                            {user?.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</div>
                                    <div>{user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : "N/A"}</div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {showScrollButton && (
                <button
                    onClick={scrollToTop}
                    className="fixed bottom-8 right-8 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-200 z-50"
                    aria-label="Scroll to top"
                >
                    <svg
                        className="w-12 h-12"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                </button>
            )}
        </div>
    );
};

export default Home;
